// src/helpers/api_helper.ts
import axios, { AxiosRequestConfig } from "axios";
import { AuthResponse, User } from "../types/api.types";

// Use fallback URL
export const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// export const IMAGE_URL = process.env.REACT_APP_IMAGE_URL || "http://localhost:5000/uploads";

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

/**
 * Normalize an Axios error into a plain Error with a useful message.
 * Prefers backend's `message`, falls back to Axios's default.
 */
const normalizeAxiosError = (error: any): Error => {
  // Network error (no response at all)
  if (!error.response) {
    const message =
      error.code === "ECONNABORTED"
        ? "Request timed out"
        : error.message || "Network error";
    const e: any = new Error(message);
    e.status = 0;
    e.code = error.code;
    return e;
  }

  const { status, data } = error.response;

  // Backend typically returns: { success, message, error }
  const backendMessage =
    (data && typeof data === "object" && (data.message || data.error)) ||
    (typeof data === "string" && data) ||
    error.message ||
    `Request failed with status ${status}`;

  const e: any = new Error(backendMessage);
  e.status = status;
  e.response = error.response; // keep the raw response if needed
  e.data = data; // <-- convenience: err.data.message works too
  e.code = error.code;
  return e;
};
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });
  failedQueue = [];
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token management functions
export const getAccessToken = (): string | null => {
  return localStorage.getItem("accessToken");
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem("refreshToken");
};

// Set initial authorization if token exists
const initialToken = getAccessToken();
if (initialToken) {
  axiosInstance.defaults.headers.common["Authorization"] =
    `Bearer ${initialToken}`;
}

// Response interceptor for token refresh
axiosInstance.interceptors.response.use(
  (response) => response.data, // Return data directly
  async (error) => {
    const originalRequest = error.config;

    // Ignore auth endpoints
    const isAuthRequest =
      originalRequest?.url?.includes("/auth/login") ||
      originalRequest?.url?.includes("/auth/register") ||
      originalRequest?.url?.includes("/auth/refresh-token");

    if (isAuthRequest) {
      return Promise.reject(normalizeAxiosError(error));
    }

    // Only handle 401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(normalizeAxiosError(error));
    }

    // Prevent infinite loop
    if (originalRequest._retry) {
      clearAuth();
      window.location.href = "/login";
      return Promise.reject(normalizeAxiosError(error));
    }

    originalRequest._retry = true;

    // If refresh is already in progress, queue the request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            };
            resolve(axiosInstance.request(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      // Use clean axios instance for refresh to avoid interceptor loop
      const refreshAxios = axios.create({
        baseURL: API_URL,
        headers: { "Content-Type": "application/json" },
      });

      const response = await refreshAxios.post("/auth/refresh-token", {
        refreshToken,
      });

      // Handle different response structures
      let accessToken = null;
      let newRefreshToken = null;

      // Check if tokens are in response.data.data
      if (response.data?.data?.accessToken) {
        accessToken = response.data.data.accessToken;
        newRefreshToken = response.data.data.refreshToken;
      }
      // Check if tokens are directly in response.data
      else if (response.data?.accessToken) {
        accessToken = response.data.accessToken;
        newRefreshToken = response.data.refreshToken;
      }
      // Check if response is the token itself
      else if (typeof response.data === "string") {
        accessToken = response.data;
      }

      if (!accessToken) {
        throw new Error("Invalid refresh response - no access token");
      }

      // Save new tokens
      setTokens(accessToken, newRefreshToken || refreshToken);

      // Process queued requests with new token
      processQueue(null, accessToken);

      // Retry original request with new token
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${accessToken}`,
      };

      return axiosInstance.request(originalRequest);
    } catch (refreshError) {
      // Refresh failed - reject all queued requests
      processQueue(refreshError, null);
      clearAuth();
      window.location.href = "/login";
      return Promise.reject(normalizeAxiosError(error));
    } finally {
      isRefreshing = false;
    }
  },
);

export const setAuthorization = (token: string | null): void => {
  if (token) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

export const setTokens = (accessToken: string, refreshToken?: string): void => {
  localStorage.setItem("accessToken", accessToken);
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }
  setAuthorization(accessToken);
};

export const clearAuth = (): void => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  sessionStorage.removeItem("authUser");
  delete axiosInstance.defaults.headers.common["Authorization"];
};

class APIClient<T = any> {
  get = (
    url: string,
    params?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    return axiosInstance.get(url, { ...config, params }) as Promise<T>;
  };

  post = <D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    return axiosInstance.post(url, data, config) as Promise<T>;
  };

  patch = <D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    return axiosInstance.patch(url, data, config) as Promise<T>;
  };

  put = <D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    return axiosInstance.put(url, data, config) as Promise<T>;
  };

  delete = (url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.delete(url, config) as Promise<T>;
  };

  upload = (
    url: string,
    formData: FormData,
    onProgress?: (progress: number) => void,
  ): Promise<T> => {
    return axiosInstance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    }) as Promise<T>;
  };
}

export const getLoggedinUser = (): User | null => {
  const user =
    localStorage.getItem("user") || sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  }
  try {
    return JSON.parse(user) as User;
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

export const updateUserData = (userData: User): void => {
  const userString = JSON.stringify(userData);
  localStorage.setItem("user", userString);
  sessionStorage.setItem("authUser", userString);
};

// Export instances
export const authApi = new APIClient<AuthResponse>();
export const apiClient = new APIClient();

export { APIClient };
